
import { createRouter, createWebHistory } from 'vue-router';
import store from '@/store';

// Authentication Pages
const LoginPage = () => import('@/views/LoginPage/index.vue');
const RegistrationPage = () => import('@/views/RegistrationPage/index.vue');
const ResetPassword = () => import('@/views/ResetPassword/index.vue');

// Dashboard and authenticated pages (lazy loaded)
const Dashboard = () => import('@/views/Dashboard/index.vue');
const NotFoundPage = () => import('@/views/NotFoundPage/index.vue');
const VerifyEmail = () => import('@/views/VerifyEmail/index.vue');
const CreateAppPage = () => import('@/views/CreateAppPage/index.vue');
const AppsPage = () => import('@/views/AppsPage/index.vue');
const AppConfigurationView = () => import('@/views/AppConfigurationView/index.vue');
const AppStatsPage = () => import('@/views/AppStatsPage/index.vue');
const MyWorkspacesPage = () => import('@/views/MyWorkspacesPage/index.vue');

// My Account pages
const MyAccount = () => import('@/views/MyAccount/index.vue');
const AccountOverview = () => import('@/views/MyAccount/AccountOverview.vue');
const MyPlans = () => import('@/views/MyAccount/MyPlans.vue');
const MyProfile = () => import('@/views/MyAccount/MyProfile.vue');
const ChangePassword = () => import('@/views/MyAccount/ChangePassword.vue');
const BillingDetails = () => import('@/views/MyAccount/BillingDetails.vue');
const Invoices = () => import('@/views/MyAccount/Invoices.vue');
const Settings = () => import('@/views/MyAccount/Settings.vue');
const DeletionSent = () => import('@/views/MyAccount/DeletionSent.vue');
const DeletionConfirm = () => import('@/views/MyAccount/DeletionConfirm.vue');
const DeletionExpired = () => import('@/views/MyAccount/DeletionExpired.vue');
const SubscriptionSuccess = () => import('@/views/MyAccount/SubscriptionSuccess.vue');
const SubscriptionCancel = () => import('@/views/MyAccount/SubscriptionCancel.vue');

// Routes configuration
const routes = [
  {
    path: '/',
    redirect: '/dashboard'
  },
  {
    path: '/login',
    name: 'Login',
    component: LoginPage,
    meta: {
      title: 'Sign In - Playtime',
      requiresAuth: false,
      noAuthOnly: true
    }
  },
  {
    path: '/register',
    name: 'Register',
    component: RegistrationPage,
    meta: {
      title: 'Create Account - Playtime',
      requiresAuth: false,
      noAuthOnly: true
    }
  },
  {
    path: '/verify',
    name: 'VerifyEmail',
    component: VerifyEmail,
    meta: {
      title: 'Verify Your Email - Playtime',
      requiresAuth: false
    }
  },
  {
    path: '/password/reset-request',
    name: 'ResetPasswordRequest',
    component: ResetPassword,
    meta: {
      title: 'Reset Password - Playtime',
      requiresAuth: false,
      noAuthOnly: true
    }
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: ResetPassword,
    meta: {
      title: 'Set New Password - Playtime',
      requiresAuth: false,
      noAuthOnly: true
    }
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: Dashboard,
    meta: {
      title: 'Dashboard - Playtime',
      requiresAuth: true
    }
  },
  {
    path: '/apps',
    name: 'Apps',
    component: AppsPage,
    meta: {
      title: 'Apps - Playtime',
      requiresAuth: true
    }
  },
  {
    path: '/apps/create',
    name: 'CreateApp',
    component: CreateAppPage,
    meta: {
      title: 'Create New App - Playtime',
      requiresAuth: true
    }
  },
  {
    path: '/apps/:appId',
    name: 'AppConfiguration',
    component: AppConfigurationView,
    meta: {
      title: 'App Configuration - Playtime',
      requiresAuth: true
    }
  },
  {
    path: '/apps/:appId/stats',
    name: 'AppStats',
    component: AppStatsPage,
    meta: {
      title: 'App Statistics - Playtime',
      requiresAuth: true
    }
  },
  {
    path: '/my-workspaces',
    name: 'MyWorkspaces',
    component: MyWorkspacesPage,
    meta: {
      title: 'My Workspaces - Playtime',
      requiresAuth: true
    }
  },
  // My Account routes
  {
    path: '/my-account',
    name: 'MyAccount',
    component: MyAccount,
    meta: {
      title: 'My Account - Playtime',
      requiresAuth: true
    }
  },
  {
    path: '/my-account/overview',
    name: 'AccountOverview',
    component: AccountOverview,
    meta: {
      title: 'Account Overview - Playtime',
      requiresAuth: true
    }
  },
  {
    path: '/my-account/plans',
    name: 'MyPlans',
    component: MyPlans,
    meta: {
      title: 'My Plans - Playtime',
      requiresAuth: true
    }
  },
  {
    path: '/my-account/profile',
    name: 'MyProfile',
    component: MyProfile,
    meta: {
      title: 'My Profile - Playtime',
      requiresAuth: true
    }
  },
  {
    path: '/my-account/change-password',
    name: 'ChangePassword',
    component: ChangePassword,
    meta: {
      title: 'Change Password - Playtime',
      requiresAuth: true
    }
  },
  {
    path: '/my-account/billing-details',
    name: 'BillingDetails',
    component: BillingDetails,
    meta: {
      title: 'Billing Details - Playtime',
      requiresAuth: true
    }
  },
  {
    path: '/my-account/invoices',
    name: 'Invoices',
    component: Invoices,
    meta: {
      title: 'Invoices - Playtime',
      requiresAuth: true
    }
  },
  {
    path: '/my-account/settings',
    name: 'Settings',
    component: Settings,
    meta: {
      title: 'Settings - Playtime',
      requiresAuth: true
    }
  },
  {
    path: '/my-account/settings/deletion-sent',
    name: 'DeletionSent',
    component: DeletionSent,
    meta: {
      title: 'Confirmation Email Sent - Playtime',
      requiresAuth: true
    }
  },
  {
    path: '/delete/confirm',
    name: 'DeletionConfirm',
    component: DeletionConfirm,
    meta: {
      title: 'Confirm Account Deletion - Playtime',
      requiresAuth: false
    }
  },
  {
    path: '/delete/expired',
    name: 'DeletionExpired',
    component: DeletionExpired,
    meta: {
      title: 'Link Expired - Playtime',
      requiresAuth: false
    }
  },
  {
    path: '/account/subscription/success',
    name: 'SubscriptionSuccess',
    component: SubscriptionSuccess,
    meta: {
      title: 'Subscription Activated - Playtime',
      requiresAuth: true
    }
  },
  {
    path: '/account/subscription/cancel',
    name: 'SubscriptionCancel',
    component: SubscriptionCancel,
    meta: {
      title: 'Checkout Cancelled - Playtime',
      requiresAuth: true
    }
  },
  {
    path: '/:catchAll(.*)',
    name: 'NotFound',
    component: NotFoundPage,
    meta: {
      title: 'Page Not Found - Playtime'
    }
  }
];

// Create router instance
const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { top: 0 };
    }
  }
});

// Navigation guards
router.beforeEach(async (to, from, next) => {
  try {
    // Update page title
    document.title = to.meta.title || 'Playtime';

    // Check authentication status
    const isLoggedIn = store.getters['auth/isLoggedIn'];
    const requiresAuth = to.matched.some(record => record.meta.requiresAuth);
    const noAuthOnly = to.matched.some(record => record.meta.noAuthOnly);

    // 🔥 Check for OAuth tokens in dashboard route
    const hasOAuthTokens = to.path === '/dashboard' && to.query.token && to.query.refreshToken;

    console.log('🛡️ Route Guard:', {
      path: to.path,
      isLoggedIn,
      requiresAuth,
      noAuthOnly,
      hasOAuthTokens
    });

    // If route requires authentication and user is not logged in
    if (requiresAuth && !isLoggedIn) {
      // 🔥 Allow dashboard access if OAuth tokens are present
      if (hasOAuthTokens) {
        console.log('✅ OAuth tokens detected, allowing dashboard access');
        next(); // Let Dashboard.vue handle the token processing
        return;
      }

      try {
        console.log('🔄 Checking authentication...');
        
        // Try to refresh the token if possible
        const refreshed = await store.dispatch('auth/checkAuth');

        if (refreshed) {
          console.log('✅ Authentication refreshed successfully');
          // User is now authenticated, proceed to requested route
          next();
        } else {
          console.log('❌ Authentication refresh failed, redirecting to login');
          // Authentication refresh failed, redirect to login
          next({
            path: '/login',
            query: { redirect: to.fullPath } // Save the intended destination
          });
        }
      } catch (error) {
        console.error('❌ Authentication check failed:', error);
        // Authentication failed, redirect to login
        next({
          path: '/login',
          query: { redirect: to.fullPath } // Save the intended destination
        });
      }
    }
    // If route is for non-authenticated users only and user is logged in
    else if (noAuthOnly && isLoggedIn) {
      console.log('🔄 User already logged in, redirecting to dashboard');
      next({ path: '/dashboard' }); // Redirect to dashboard
    }
    // All other cases, proceed normally
    else {
      console.log('✅ Route access granted');
      next();
    }
  } catch (error) {
    console.error('❌ Route guard error:', error);
    // Fallback: allow navigation to continue
    next();
  }
});

// Global after hook for additional logging (optional)
router.afterEach((to, from) => {
  console.log('📍 Navigation completed:', {
    from: from.path,
    to: to.path,
    query: to.query
  });
});

// Handle navigation errors (optional)
router.onError((error) => {
  console.error('🚨 Router error:', error);
});

export default router;